import { useEffect, useState } from 'react';
import { api } from '../api.js';
import { timeAgo } from '../util/time.js';

// T087 — Morning brief panel. Shows on top of the dashboard. Fetches
// /api/ai/morning-brief on mount; the backend serves a cached copy if
// one was generated in the last 12h, otherwise generates a fresh one
// (one Claude call).
//
// "Refresh" button forces a regen (?refresh=true). Quietly skips
// itself if the operator has no Anthropic key (the API would 400
// anyway, but we don't even try).

export default function MorningBriefPanel({ aiKeyReady }) {
  const [brief, setBrief] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [collapsed, setCollapsed] = useState(false);

  const load = async (refresh = false) => {
    if (!aiKeyReady) return;
    setLoading(true);
    setError('');
    try {
      const r = await api.get(`/ai/morning-brief${refresh ? '?refresh=true' : ''}`);
      setBrief(r);
    } catch (e) {
      setError(e?.body?.message || e?.message || 'Could not load brief');
    } finally {
      setLoading(false);
    }
  };

  // Auto-load once on mount when the key is ready.
  useEffect(() => {
    if (aiKeyReady && !brief && !loading && !error) load(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aiKeyReady]);

  if (!aiKeyReady) return null;
  if (loading && !brief) {
    return (
      <section className="mt-4 rounded-xl border border-brand-700 bg-brand-600/5 p-4">
        <p className="text-sm text-brand-300">
          <span className="mr-2 inline-block h-2 w-2 animate-pulse rounded-full bg-brand-500" />
          Claude is reading your dashboard and drafting today's brief…
        </p>
      </section>
    );
  }
  if (error && !brief) {
    return (
      <section className="mt-4 rounded-xl border border-amber-700 bg-amber-950/15 p-4">
        <p className="text-sm text-amber-300">⚠ {error}</p>
        <button
          type="button"
          onClick={() => load(false)}
          className="mt-2 rounded-md border border-slate-700 px-3 py-1 text-xs text-slate-300 hover:border-brand-600 hover:text-brand-400"
        >
          Retry
        </button>
      </section>
    );
  }
  if (!brief) return null;

  return (
    <section className="mt-4 rounded-xl border border-brand-700 bg-brand-600/5 p-4">
      {/* T290 — a failed REGENERATION used to be silent. The error branch above
          is `error && !brief`, so once a brief was on screen a failed refresh
          rendered nothing at all: the operator pressed regenerate, the request
          failed, and the panel went on showing yesterday's brief with its own
          timestamp as if nothing had happened. The stale content stays — it is
          still the last true thing — but it now says the refresh did not land. */}
      {error && (
        <p className="mb-2 rounded-md border border-amber-800/60 bg-amber-950/20 px-2.5 py-1.5 text-xs text-amber-200">
          ⚠ Could not refresh: {error} — showing the last brief.
        </p>
      )}
      <div className="flex items-baseline justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-widest text-brand-400">Morning brief · {timeAgo(brief.generatedAt)}{brief.cached ? ' · cached' : ''}</p>
          {brief.headline && <p className="mt-0.5 text-base font-semibold text-slate-100">{brief.headline}</p>}
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          <button
            type="button"
            onClick={() => load(true)}
            disabled={loading}
            className="rounded border border-slate-700 px-2 py-0.5 text-[10px] text-slate-400 hover:border-brand-600 hover:text-brand-400 disabled:opacity-50"
            title="Regenerate the brief (costs ~$0.01)"
          >
            {loading ? '…' : '↻'}
          </button>
          <button
            type="button"
            onClick={() => setCollapsed((v) => !v)}
            className="rounded border border-slate-700 px-2 py-0.5 text-[10px] text-slate-400 hover:border-brand-600 hover:text-brand-400"
          >
            {collapsed ? '▾' : '▴'}
          </button>
        </div>
      </div>
      {!collapsed && (
        <>
          {brief.summary && <p className="mt-2 text-sm leading-relaxed text-slate-200">{brief.summary}</p>}
          {brief.actions?.length > 0 && (
            <ul className="mt-3 space-y-1.5">
              {brief.actions.map((a, i) => (
                <li key={i} className="flex items-baseline gap-2 text-sm">
                  <span className="shrink-0 font-mono text-[10px] text-brand-400">{i + 1}.</span>
                  <div className="min-w-0">
                    <p className="text-slate-100">{a.label}</p>
                    {a.why && <p className="text-[11px] italic text-slate-500">{a.why}</p>}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </section>
  );
}
